import { SESSION_KEYS } from './signOut.js';

/**
 * The closed-beta gate (ENG-913, backend `Documentation/beta-access-gate.md`).
 * With the gate on, an account that was not invited gets no session:
 *
 * - the Google, Twitch and dev callbacks answer 403
 *   `{"error":"beta_waitlisted","params":{"status":"pending"|"rejected"}}`;
 * - the Apple callback redirects to `/oauth/apple?error=beta_waitlisted&status=…`;
 * - a request carrying an old token answers 401 `beta_waitlisted` (or
 *   `session_revoked`).
 *
 * Each of those lands on the `/waitlist` page (ENG-915). With the gate off the
 * backend never sends the code, and nothing here runs.
 */
export const BETA_WAITLISTED = 'beta_waitlisted';

export const WAITLIST_PATH = '/waitlist';

export const WAITLIST_STATUSES = Object.freeze(['pending', 'rejected']);

/** `pending` or `rejected`; anything else (missing, unknown) reads as pending. */
export const normalizeWaitlistStatus = (status) =>
  (typeof status === 'string' && WAITLIST_STATUSES.includes(status.toLowerCase()) ? status.toLowerCase() : 'pending');

/** Whether a fetchWrapper rejection is the beta gate's refusal. */
export const isBetaWaitlisted = (error) => error?.code === BETA_WAITLISTED;

/** The waitlist status a fetchWrapper rejection carries (`params.status`). */
export const waitlistStatusOf = (error) => normalizeWaitlistStatus(error?.params?.status);

/**
 * `/waitlist?status=…[&provider=…]`. The status travels in the URL so a
 * refresh keeps it; the provider is only for the "Signed up with …" line and
 * is left out when it is not known (a 401 on an old session).
 */
export const waitlistPath = (status, provider) => {
  const params = new URLSearchParams({ status: normalizeWaitlistStatus(status) });
  if (typeof provider === 'string' && provider !== '') params.set('provider', provider);
  return `${WAITLIST_PATH}?${params.toString()}`;
};

/** Drop every trace of a session, so a waitlisted browser holds no token. */
export const clearSessionStorage = () => {
  SESSION_KEYS.forEach((key) => {
    try {
      sessionStorage.removeItem(key);
    } catch {
      // Storage unavailable: nothing is held there either.
    }
  });
};
